import React, { useMemo } from 'react';
import PropTypes from 'prop-types';

export const OptimizerChart = React.memo(function OptimizerChart({ shotGroups }) {
  // Memoize expensive calculations
  const { baselineMetrics, testGroups, comparisonData, isEmpty } = useMemo(() => {
    const baseline = shotGroups.find(g => g.isBaseline) || shotGroups[0];
    const tests = shotGroups.filter(g => !g.isBaseline);

    if (!baseline) {
      return { baselineMetrics: null, testGroups: [], comparisonData: [], isEmpty: true };
    }

    const calculateGroupMetrics = (group) => {
      const includedShots = group.shots.filter(s => s.included);
      if (includedShots.length === 0) return null;

      const avg = (key) => includedShots.reduce((sum, s) => sum + (s.metrics[key] || 0), 0) / includedShots.length;
      const stdDev = (key) => {
        const mean = avg(key);
        const variance = includedShots.reduce((sum, s) => sum + Math.pow((s.metrics[key] || 0) - mean, 2), 0) / includedShots.length;
        return Math.sqrt(variance);
      };

      return {
        name: group.name,
        color: group.color,
        carry: avg('carry'),
        total: avg('total'),
        ballSpeed: avg('ballSpeed'),
        launchAngle: avg('launchAngle'),
        spinRate: avg('spinRate'),
        landingAngle: avg('landingAngle'),
        carryStdDev: stdDev('carry'),
        totalStdDev: stdDev('total'),
      };
    };

    const baseMetrics = calculateGroupMetrics(baseline);
    const testMetrics = tests.map(calculateGroupMetrics).filter(m => m !== null);

    if (!baseMetrics) {
      return { baselineMetrics: null, testGroups: [], comparisonData: [], isEmpty: true };
    }

    // Build comparison data for bar charts
    const comparison = testMetrics.map(test => ({
      name: test.name,
      carryDiff: test.carry - baseMetrics.carry,
      totalDiff: test.total - baseMetrics.total,
      ballSpeedDiff: test.ballSpeed - baseMetrics.ballSpeed,
      launchAngleDiff: test.launchAngle - baseMetrics.launchAngle,
      spinRateDiff: test.spinRate - baseMetrics.spinRate,
      testColor: test.color,
    }));

    return {
      baselineMetrics: baseMetrics,
      testGroups: testMetrics,
      comparisonData: comparison,
      isEmpty: false,
    };
  }, [shotGroups]);

  if (isEmpty || !baselineMetrics) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 p-6 shadow-sm">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Optimizer - Performance Comparison</h2>
        <div className="flex items-center justify-center h-[400px] text-gray-600 bg-gray-50 rounded-lg">
          <div className="text-center">
            <p className="text-lg font-semibold mb-2 text-gray-700">No comparison data available</p>
            <p className="text-sm text-gray-500">Mark one group as baseline and include shots to compare</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-6 shadow-sm mb-6">
      <h2 className="text-lg font-semibold text-gray-900 mb-6">Optimizer - Performance Comparison</h2>

      {/* Big Number Panel - Baseline Metrics */}
      <div className="mb-8 bg-gradient-to-r from-orange-50 to-orange-50/50 rounded-lg border-2 border-orange-300 p-6">
        <div className="flex items-center gap-2 mb-4">
          <div className="w-4 h-4 rounded-full bg-orange-500" />
          <h3 className="text-lg font-bold text-gray-900">Baseline: {baselineMetrics.name}</h3>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <div className="text-center">
            <div className="text-3xl font-bold text-orange-600 font-mono">{baselineMetrics.carry.toFixed(1)}</div>
            <div className="text-xs text-gray-600 mt-1">Carry (yds)</div>
          </div>
          <div className="text-center">
            <div className="text-3xl font-bold text-orange-600 font-mono">{baselineMetrics.total.toFixed(1)}</div>
            <div className="text-xs text-gray-600 mt-1">Total (yds)</div>
          </div>
          <div className="text-center">
            <div className="text-3xl font-bold text-orange-600 font-mono">{baselineMetrics.ballSpeed.toFixed(1)}</div>
            <div className="text-xs text-gray-600 mt-1">Ball Speed (mph)</div>
          </div>
          <div className="text-center">
            <div className="text-3xl font-bold text-orange-600 font-mono">{baselineMetrics.launchAngle.toFixed(1)}°</div>
            <div className="text-xs text-gray-600 mt-1">Launch Angle</div>
          </div>
          <div className="text-center">
            <div className="text-3xl font-bold text-orange-600 font-mono">{baselineMetrics.spinRate.toFixed(0)}</div>
            <div className="text-xs text-gray-600 mt-1">Spin Rate (rpm)</div>
          </div>
        </div>
      </div>

      {/* Comparison Charts - Dual Bars */}
      {comparisonData.length > 0 ? (
        <div className="space-y-6">
          {comparisonData.map((data, idx) => (
            <div key={data.name} className="bg-gray-50 rounded-lg border border-gray-300 p-4">
              <div className="flex items-center gap-2 mb-4">
                <div className="w-3 h-3 rounded-full" style={{ backgroundColor: data.testColor }} />
                <h4 className="font-semibold text-gray-900">{data.name} vs Baseline</h4>
              </div>

              {/* Metrics Grid with Dual Bars */}
              <div className="space-y-3">
                {/* Carry Distance */}
                <div>
                  <div className="flex justify-between items-center mb-1">
                    <span className="text-xs text-gray-600">Carry Distance</span>
                    <span className={`text-sm font-mono font-semibold ${data.carryDiff >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                      {data.carryDiff >= 0 ? '+' : ''}{data.carryDiff.toFixed(1)} yds
                    </span>
                  </div>
                  <div className="h-8 flex items-center gap-2">
                    <div className="flex-1 bg-gray-200 rounded overflow-hidden relative h-6">
                      <div
                        className="absolute top-0 left-0 h-full bg-orange-500"
                        style={{ width: '50%' }}
                      />
                      <div
                        className={`absolute top-0 h-full ${data.carryDiff >= 0 ? 'bg-blue-500' : 'bg-red-500'}`}
                        style={{
                          left: '50%',
                          width: `${Math.abs(data.carryDiff) / baselineMetrics.carry * 50}%`,
                          transform: data.carryDiff < 0 ? 'scaleX(-1)' : 'none',
                          transformOrigin: 'left',
                        }}
                      />
                    </div>
                  </div>
                </div>

                {/* Total Distance */}
                <div>
                  <div className="flex justify-between items-center mb-1">
                    <span className="text-xs text-gray-600">Total Distance</span>
                    <span className={`text-sm font-mono font-semibold ${data.totalDiff >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                      {data.totalDiff >= 0 ? '+' : ''}{data.totalDiff.toFixed(1)} yds
                    </span>
                  </div>
                  <div className="h-8 flex items-center gap-2">
                    <div className="flex-1 bg-gray-200 rounded overflow-hidden relative h-6">
                      <div
                        className="absolute top-0 left-0 h-full bg-orange-500"
                        style={{ width: '50%' }}
                      />
                      <div
                        className={`absolute top-0 h-full ${data.totalDiff >= 0 ? 'bg-blue-500' : 'bg-red-500'}`}
                        style={{
                          left: '50%',
                          width: `${Math.abs(data.totalDiff) / baselineMetrics.total * 50}%`,
                          transform: data.totalDiff < 0 ? 'scaleX(-1)' : 'none',
                          transformOrigin: 'left',
                        }}
                      />
                    </div>
                  </div>
                </div>

                {/* Ball Speed */}
                <div>
                  <div className="flex justify-between items-center mb-1">
                    <span className="text-xs text-gray-600">Ball Speed</span>
                    <span className={`text-sm font-mono font-semibold ${data.ballSpeedDiff >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                      {data.ballSpeedDiff >= 0 ? '+' : ''}{data.ballSpeedDiff.toFixed(1)} mph
                    </span>
                  </div>
                  <div className="h-8 flex items-center gap-2">
                    <div className="flex-1 bg-gray-200 rounded overflow-hidden relative h-6">
                      <div
                        className="absolute top-0 left-0 h-full bg-orange-500"
                        style={{ width: '50%' }}
                      />
                      <div
                        className={`absolute top-0 h-full ${data.ballSpeedDiff >= 0 ? 'bg-blue-500' : 'bg-red-500'}`}
                        style={{
                          left: '50%',
                          width: `${Math.abs(data.ballSpeedDiff) / baselineMetrics.ballSpeed * 50}%`,
                          transform: data.ballSpeedDiff < 0 ? 'scaleX(-1)' : 'none',
                          transformOrigin: 'left',
                        }}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="text-center text-gray-600 bg-gray-50 rounded-lg py-8">
          <p>No test groups to compare against baseline</p>
        </div>
      )}
    </div>
  );
});

OptimizerChart.propTypes = {
  shotGroups: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired,
      color: PropTypes.string.isRequired,
      isBaseline: PropTypes.bool,
      shots: PropTypes.arrayOf(
        PropTypes.shape({
          included: PropTypes.bool.isRequired,
          metrics: PropTypes.shape({
            carry: PropTypes.number,
            total: PropTypes.number,
            ballSpeed: PropTypes.number,
            launchAngle: PropTypes.number,
            spinRate: PropTypes.number,
            landingAngle: PropTypes.number,
          }).isRequired,
        })
      ).isRequired,
    })
  ).isRequired,
};
